import React, { useEffect, useRef } from 'react';
import { XIcon } from 'lucide-react';
interface DialogProps {
  open: boolean;
  onClose: () => void;
  id: string;
  title: string;
  children: React.ReactNode;
  variant?: 'center' | 'drawer';
  closeLabel?: string;
}
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
export function Dialog({
  open,
  onClose,
  id,
  title,
  children,
  variant = 'center',
  closeLabel = 'Close'
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const trigger = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const getFocusable = () => panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
    const first = getFocusable()[0];
    (first ?? panel)?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = getFocusable();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (trigger && document.contains(trigger)) trigger.focus();
    };
  }, [open]);
  if (!open) return null;
  const panelClass = variant === 'drawer' ? 'anim-enter ml-auto flex h-full w-full max-w-md flex-col bg-cream shadow-xl' : 'anim-pop m-auto flex max-h-[88vh] w-[calc(100%-2rem)] max-w-lg flex-col rounded-3xl bg-cream shadow-xl';
  return <div className="fixed inset-0 z-50 flex" data-id="element-403" data-mp="f6b8n84eyfcu">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" aria-hidden="true" onClick={() => onCloseRef.current()} data-id="element-404" data-mp="f74g6bb758w1" />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} tabIndex={-1} className={`relative z-10 border border-line hc-strong ${panelClass}`} data-id="element-405" data-mp="k7if93utl8hx">
        <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4" data-id="element-406" data-mp="y4x70f8jpsd2">
          <h2 id={`${id}-title`} className="font-display text-2xl font-semibold text-forest" data-id="element-407" data-mp="2vv8aqhx1de4i">
            {title}
          </h2>
          <button type="button" onClick={() => onCloseRef.current()} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-forest transition-colors duration-150 hover:bg-sand" aria-label={closeLabel} data-id="element-408" data-mp="2zxlckv8nwrw0">
            <XIcon size={20} aria-hidden="true" data-id="element-409" data-mp="2img7fxljlf5w" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 text-ink" data-id="element-410" data-mp="2elwaau0yybjr">{children}</div>
      </div>
    </div>;
}